import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { ClientApiError, useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { listConfirmedDumpsEndpoint } from "../client-messages/confirmed-dumps";
import type { ConfirmedDumpItem, ListConfirmedDumpsResponse } from "../client-messages/confirmed-dumps";

/**
 * Page size for the camera confirmed-dumps list. A camera slot accrues dumps
 * slowly (each is a manual operator confirmation), so 20 comfortably covers the
 * common case on the first page while keeping "Load more" available for busy
 * slots. The endpoint caps at 100.
 */
const PAGE_LIMIT = 20;

export interface UseCameraConfirmedDumpsResult {
  readonly dumps: readonly ConfirmedDumpItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
}

export interface UseCameraConfirmedDumpsOptions {
  siteId: string;
  cameraKey: string;
  /** Gate the query (e.g. behind the support-user + feature-flag check). */
  enabled?: boolean;
}

/**
 * Lists a single camera slot's operator-confirmed dumps, newest-first.
 *
 * Pagination is cursor-based: the endpoint forwards the DynamoDB
 * `LastEvaluatedKey` as an opaque `cursor` on each page, and `useInfiniteQuery`
 * threads it through subsequent fetches (mirrors `useWasteSenseReportList`).
 * The response items carry raw `objectType` + ISO `detectedAt`/`timestamp`; the
 * consumer resolves the human object label and formats the date itself (the
 * endpoint has no server-side timezone dependency by design).
 */
export function useCameraConfirmedDumps(options: UseCameraConfirmedDumpsOptions): UseCameraConfirmedDumpsResult {
  const apiConfig = useApiConfig();
  const tenant = apiConfig.tenant;
  const enabled = (options.enabled ?? true) && !!tenant;

  const query = useInfiniteQuery<
    ListConfirmedDumpsResponse,
    Error,
    InfiniteData<ListConfirmedDumpsResponse, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: ["dumping", "confirmed-dumps", tenant?.tenantId, options.siteId, options.cameraKey, PAGE_LIMIT] as const,
    enabled,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    queryFn: async ({ pageParam, signal }) => {
      // `enabled` gates the call; this defensively narrows the optional tenant.
      if (!tenant) throw new Error("Tenant not ready");
      const path = {
        tenantId: tenant.tenantId,
        siteId: SiteId.fromString(options.siteId),
        assetCameraKey: AssetCameraKey.fromString(options.cameraKey),
      };
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listConfirmedDumpsEndpoint, {
          path,
          query: { limit: PAGE_LIMIT, ...(pageParam !== undefined && { cursor: pageParam }) },
        });

      const response = await apiConfig.authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              message = json.message ?? text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with the default message
        }
        throw new ClientApiError(response.status, message);
      }
      const data: unknown = await response.json();
      return listConfirmedDumpsEndpoint.responseSchema.parse(data);
    },
  });

  const dumps = useMemo<readonly ConfirmedDumpItem[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  return {
    dumps,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
